import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { first, switchMap, tap } from 'rxjs/operators';

import { MessagesConstants } from '@shared/constants/messages.constants';
import {
  CreateDriverBlendedSiteDistributionInput,
  DriverSiteGroup,
  GqlService,
  UpdateDriverBlendedSiteDistributionInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { SiteCurveModel, SiteCurveStore } from './site-curve.store';

@Injectable({ providedIn: 'root' })
export class SiteCurveService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private siteCurveStore = inject(SiteCurveStore);

  async createBlendedSiteCurve(site: CreateDriverBlendedSiteDistributionInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createDriverBlendedSiteDistribution$(site)
    );

    if (success && data) {
      await firstValueFrom(this.get().pipe(first()));
      this.overlayService.success(MessagesConstants.SITE_CURVES.SUCCESSFULLY_CREATED);
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.siteCurveStore.remove(() => true);

        return this.gqlService.listDriverSiteGroups$().pipe(
          tap(({ data, errors, success }) => {
            let flag = false;

            if (success && data && data?.length > 0) {
              this.siteCurveStore.set(
                <SiteCurveModel[]>data
                  .filter((y) => y.curve_type === 'NET')
                  .map((x) => {
                    if (!x.is_blended && !flag) {
                      flag = true;

                      return { ...x, showLine: true };
                    }

                    return { ...x, showLine: false };
                  })
              );
            } else if (!success) {
              this.overlayService.error(errors);
            }
          })
        );
      })
    );
  }

  async removeSiteCurve(driverSiteGroup: DriverSiteGroup) {
    let successOverall = false;
    let errorsOverall: string | string[] | undefined = [];

    if (driverSiteGroup.is_blended && driverSiteGroup.site_group_id) {
      const { errors, success } = await firstValueFrom(
        this.gqlService.removeDriverBlendedSiteDistribution$(driverSiteGroup.site_group_id)
      );

      successOverall = success;
      errorsOverall = errors;
    } else if (!driverSiteGroup.is_blended && driverSiteGroup.driver_setting_id) {
      const { errors, success } = await firstValueFrom(
        this.gqlService.removeSiteGroupDriver$(driverSiteGroup.driver_setting_id)
      );

      successOverall = success;
      errorsOverall = errors;
    }

    if (successOverall) {
      await firstValueFrom(this.get().pipe(first()));
      this.overlayService.success(`${driverSiteGroup.name} successfully removed!`);
    } else {
      this.overlayService.error(errorsOverall);
    }

    return { errorsOverall, successOverall };
  }

  async updateBlendedSiteCurve(site: UpdateDriverBlendedSiteDistributionInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateDriverBlendedSiteDistribution$(site)
    );

    if (success && data) {
      await firstValueFrom(this.get().pipe(first()));
      this.overlayService.success(`${site.name} successfully updated!`);
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }
}
